import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, CheckCircle2, XCircle, Archive, FileText, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { API, useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const PERMISSION_OPTIONS = [
  { value: 'own', label: 'I OWN THIS CONTENT (ମୋର ନିଜର)' },
  { value: 'permitted', label: 'I HAVE PERMISSION TO USE (ଅନୁମତି ମିଳିଛି)' },
  { value: 'public_domain', label: 'PUBLIC DOMAIN / OPEN LICENSE' },
  { value: 'authorized_official', label: 'AUTHORIZED OFFICIAL RESOURCE' },
];

export default function AdminUploadPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('upload'); // upload | queue
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', class_num: 5, medium: 'odia', subject_name: '', textbook: '',
    chapter: '', topic: '', academic_session: '2025-26', language: 'odia', resource_type: 'pdf',
    tags: '', source: '', source_url: '', permission_status: 'own', license: '', visibility: 'public',
  });
  const [uploading, setUploading] = useState(false);
  const [resources, setResources] = useState([]);
  const [filter, setFilter] = useState('pending');

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadResources = React.useCallback(() => {
    axios.get(`${API}/admin/resources`, { params: { status: filter }, withCredentials: true })
      .then(r => setResources(r.data))
      .catch(e => toast({ title: 'Error', description: e?.response?.data?.detail || 'Not authorized' }));
  }, [filter]);

  useEffect(() => { if (tab === 'queue') loadResources(); }, [tab, filter, loadResources]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast({ title: 'File required' }); return; }
    if (!form.title || !form.subject_name) { toast({ title: 'Title & Subject required' }); return; }
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
    setUploading(true);
    try {
      const res = await axios.post(`${API}/admin/resources/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Uploaded ✅', description: `${res.data.title} — pending verification` });
      setFile(null);
      setForm(f => ({ ...f, title: '', description: '', chapter: '', topic: '' }));
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setUploading(false); }
  };

  const verify = async (id, status) => {
    const fd = new FormData();
    fd.append('status', status);
    try {
      await axios.post(`${API}/admin/resources/${id}/verify`, fd, { withCredentials: true });
      toast({ title: `Marked ${status} ✅` });
      loadResources();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed' });
    }
  };

  if (!user) return (
    <div className="p-6 text-center text-sm text-gray-600">Please <Link className="text-emerald-600 font-bold" to="/">login</Link> first.</div>
  );

  return (
    <div className="px-4 pt-4 space-y-4 pb-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium" data-testid="admin-upload-back">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f97316 60%, #f59e0b 100%)' }}>
        <Upload size={26} />
        <h1 className="text-xl font-extrabold mt-2">🔒 Admin · Resource Upload</h1>
        <p className="text-xs text-white/90 mt-1">Authorized content only · Copyright-safe workflow</p>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab('upload')} data-testid="tab-upload"
          className={`flex-1 py-2 rounded-xl text-sm font-bold ${tab === 'upload' ? 'bg-red-600 text-white' : 'bg-white border-2 border-gray-200 text-gray-700'}`}>
          📤 Upload
        </button>
        <button onClick={() => setTab('queue')} data-testid="tab-queue"
          className={`flex-1 py-2 rounded-xl text-sm font-bold ${tab === 'queue' ? 'bg-red-600 text-white' : 'bg-white border-2 border-gray-200 text-gray-700'}`}>
          📋 Verification Queue
        </button>
      </div>

      {tab === 'upload' && (
        <form onSubmit={submit} className="space-y-3">
          <Card className="rounded-2xl p-4 border-amber-200 bg-amber-50">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-800 leading-relaxed">
                <strong>Copyright reminder:</strong> ଆପଣ ଉପଲୋଡ୍ କରୁଥିବା content ଉପରେ ଆପଣଙ୍କର ଅଧିକାର ଥିବା ଉଚିତ୍। ଅନୁମତି ନ ଥିଲେ upload ନ କରନ୍ତୁ। Uploader ର responsibility ହେଉଛି legal rights ର।
              </p>
            </div>
          </Card>

          <Card className="rounded-2xl p-4 border-gray-200 space-y-3">
            <div>
              <label className="text-[11px] font-bold text-gray-700">File *</label>
              <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="admin-file-input"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp,.mp3,.wav,.m4a,.mp4,.webm,.mov,.zip"
                className="mt-1 w-full text-xs" required />
              {file && <p className="text-[10px] text-gray-500 mt-1">Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</p>}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Title *" value={form.title} onChange={e => upd('title', e.target.value)} data-testid="admin-title" className="h-10 text-sm col-span-2" required />
              <Input placeholder="Description" value={form.description} onChange={e => upd('description', e.target.value)} className="h-10 text-sm col-span-2" />
              <select value={form.class_num} onChange={e => upd('class_num', parseInt(e.target.value, 10))} data-testid="admin-class" className="h-10 rounded-lg border border-gray-300 px-2 text-sm">
                {[1,2,3,4,5,6,7,8,9,10].map(c => <option key={c} value={c}>Class {c}</option>)}
              </select>
              <select value={form.medium} onChange={e => upd('medium', e.target.value)} className="h-10 rounded-lg border border-gray-300 px-2 text-sm">
                <option value="odia">ଓଡ଼ିଆ</option>
                <option value="english">English</option>
              </select>
              <Input placeholder="Subject *" value={form.subject_name} onChange={e => upd('subject_name', e.target.value)} data-testid="admin-subject" className="h-10 text-sm" required />
              <Input placeholder="Textbook" value={form.textbook} onChange={e => upd('textbook', e.target.value)} className="h-10 text-sm" />
              <Input placeholder="Chapter" value={form.chapter} onChange={e => upd('chapter', e.target.value)} className="h-10 text-sm" />
              <Input placeholder="Topic" value={form.topic} onChange={e => upd('topic', e.target.value)} className="h-10 text-sm" />
              <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={e => upd('tags', e.target.value)} className="h-10 text-sm col-span-2" />
              <Input placeholder="Source (org name)" value={form.source} onChange={e => upd('source', e.target.value)} className="h-10 text-sm" />
              <Input placeholder="Source URL" value={form.source_url} onChange={e => upd('source_url', e.target.value)} className="h-10 text-sm" />
              <Input placeholder="License (optional)" value={form.license} onChange={e => upd('license', e.target.value)} className="h-10 text-sm col-span-2" />
            </div>

            <div>
              <label className="text-[11px] font-bold text-gray-700">Permission Status *</label>
              <select value={form.permission_status} onChange={e => upd('permission_status', e.target.value)} data-testid="admin-permission"
                className="mt-1 w-full h-10 rounded-lg border border-gray-300 px-2 text-sm">
                {PERMISSION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div>
              <label className="text-[11px] font-bold text-gray-700">Visibility</label>
              <select value={form.visibility} onChange={e => upd('visibility', e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-gray-300 px-2 text-sm">
                <option value="public">Public (Everyone)</option>
                <option value="teachers">Teachers only</option>
                <option value="admin_only">Admin only</option>
              </select>
            </div>

            <Button type="submit" disabled={uploading} data-testid="admin-upload-btn"
              className="w-full h-12 rounded-xl bg-red-600 hover:bg-red-700 text-white font-extrabold text-base">
              {uploading ? 'Uploading...' : '📤 Upload (starts as PENDING)'}
            </Button>
          </Card>
        </form>
      )}

      {tab === 'queue' && (
        <>
          <div className="flex gap-2 text-xs">
            {['pending', 'verified', 'rejected', 'archived', 'all'].map(s => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
                className={`px-3 py-1.5 rounded-full font-bold ${filter === s ? 'bg-red-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}>
                {s}
              </button>
            ))}
          </div>
          {resources.length === 0 ? (
            <Card className="rounded-2xl p-6 text-center border-gray-200">
              <div className="text-3xl">📭</div>
              <div className="text-sm font-bold text-gray-700 mt-2">No resources in "{filter}" state</div>
            </Card>
          ) : (
            <div className="space-y-2">
              {resources.map(r => (
                <Card key={r.id} className="rounded-2xl p-3 border-gray-200" data-testid={`res-${r.id}`}>
                  <div className="flex items-start gap-3">
                    <FileText size={20} className="text-red-500 shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-gray-900 truncate">{r.title}</div>
                      <div className="text-[11px] text-gray-500 truncate">Class {r.class} · {r.subject_name} · {r.chapter || '—'}</div>
                      <div className="text-[10px] text-gray-400 mt-1">
                        Permission: <span className="font-bold text-emerald-600">{r.permission_status}</span> · {(r.file_size / 1024 / 1024).toFixed(2)} MB · {r.original_filename}
                      </div>
                      <div className="text-[10px] text-gray-400">Uploaded by: {r.uploaded_by} · {new Date(r.uploaded_at).toLocaleString('en-IN')}</div>
                      <div className="mt-2 flex items-center gap-1.5">
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${
                          r.verification_status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                          r.verification_status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                          r.verification_status === 'archived' ? 'bg-gray-200 text-gray-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>{r.verification_status}</span>
                        {r.verification_status === 'pending' && (
                          <>
                            <button onClick={() => verify(r.id, 'verified')} data-testid={`approve-${r.id}`} className="text-[10px] px-2 py-1 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11}/> Verify</button>
                            <button onClick={() => verify(r.id, 'rejected')} data-testid={`reject-${r.id}`} className="text-[10px] px-2 py-1 rounded-lg bg-rose-600 text-white font-bold hover:bg-rose-700 inline-flex items-center gap-1"><XCircle size={11}/> Reject</button>
                          </>
                        )}
                        {r.verification_status !== 'archived' && (
                          <button onClick={() => verify(r.id, 'archived')} data-testid={`archive-${r.id}`} className="text-[10px] px-2 py-1 rounded-lg bg-gray-500 text-white font-bold hover:bg-gray-600 inline-flex items-center gap-1"><Archive size={11}/> Archive</button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
